/**
 * 📊 IMAGE STATUS - Status indicators for document completion
 */

import React from 'react';
import {
  Box,
  Chip,
  Typography,
  LinearProgress,
  Stack,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Upload,
  Image,
  Assignment,
  Person,
  Badge,
  DirectionsCar,
} from '@mui/icons-material';

interface ImageStatusProps {
  hasImage: boolean;
  documentType: 'nin' | 'selfie' | 'license_front' | 'license_back' | 'all';
  status?: 'approved' | 'pending' | 'rejected' | 'not_uploaded';
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const ImageStatus: React.FC<ImageStatusProps> = ({
  hasImage,
  documentType,
  status,
  showLabel = true,
  size = 'medium'
}) => {
  const getDocumentConfig = (type: string) => {
    const configs = {
      nin: {
        label: 'NIN Document',
        icon: <Assignment />,
        color: 'primary.main'
      },
      selfie: {
        label: 'Selfie Photo',
        icon: <Person />,
        color: 'secondary.main'
      },
      license_front: {
        label: 'License Front',
        icon: <Badge />,
        color: 'info.main'
      },
      license_back: {
        label: 'License Back',
        icon: <Badge />,
        color: 'info.main'
      },
      all: {
        label: 'All Documents',
        icon: <Image />,
        color: 'primary.main'
      }
    };

    return configs[type as keyof typeof configs] || configs.all;
  };

  const getStatusConfig = (hasImg: boolean, stat?: string) => {
    if (!hasImg) {
      return {
        label: 'Not Uploaded',
        color: 'error' as const,
        icon: <Upload />,
        chipColor: 'default' as const
      };
    }

    switch (stat) {
      case 'approved':
        return {
          label: 'Approved',
          color: 'success' as const,
          icon: <CheckCircle />,
          chipColor: 'success' as const
        };
      case 'rejected':
        return {
          label: 'Rejected',
          color: 'error' as const,
          icon: <Cancel />,
          chipColor: 'error' as const
        };
      case 'pending':
      default:
        return {
          label: 'Pending Review',
          color: 'warning' as const,
          icon: <Warning />,
          chipColor: 'warning' as const
        };
    }
  };

  const docConfig = getDocumentConfig(documentType);
  const statusConfig = getStatusConfig(hasImage, status);

  const getSize = () => {
    switch (size) {
      case 'small': return { avatar: 24, chip: 'small' as const };
      case 'large': return { avatar: 40, chip: 'medium' as const };
      default: return { avatar: 32, chip: 'small' as const };
    }
  };

  const sizeConfig = getSize();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Document type indicator */}
      <Tooltip title={docConfig.label}>
        <Avatar sx={{ 
          bgcolor: docConfig.color, 
          width: sizeConfig.avatar, 
          height: sizeConfig.avatar 
        }}>
          {React.cloneElement(docConfig.icon, { 
            sx: { fontSize: size === 'small' ? 14 : size === 'large' ? 20 : 16 } 
          })}
        </Avatar>
      </Tooltip>

      {/* Status chip */}
      <Chip
        icon={statusConfig.icon}
        label={showLabel ? statusConfig.label : undefined}
        color={statusConfig.chipColor}
        size={sizeConfig.chip}
        variant={statusConfig.chipColor === 'success' ? 'filled' : 'outlined'}
      />
    </Box>
  );
};

interface DocumentCompletionStatusProps {
  documents: {
    nin?: { hasImage: boolean; status?: string };
    selfie?: { hasImage: boolean; status?: string };
    license_front?: { hasImage: boolean; status?: string };
    license_back?: { hasImage: boolean; status?: string };
  };
  showDetails?: boolean;
}

export const DocumentCompletionStatus: React.FC<DocumentCompletionStatusProps> = ({
  documents,
  showDetails = false
}) => {
  const documentTypes = [
    { key: 'nin', label: 'NIN Document' },
    { key: 'selfie', label: 'Selfie Photo' },
    { key: 'license_front', label: 'License Front' },
    { key: 'license_back', label: 'License Back' }
  ];

  const totalDocs = documentTypes.length;
  const uploadedDocs = documentTypes.filter(type => 
    documents[type.key as keyof typeof documents]?.hasImage
  ).length;
  const approvedDocs = documentTypes.filter(type => 
    documents[type.key as keyof typeof documents]?.status === 'approved'
  ).length;

  const completionPercentage = Math.round((uploadedDocs / totalDocs) * 100);
  const approvalPercentage = Math.round((approvedDocs / totalDocs) * 100);

  return (
    <Box>
      {/* Overall status */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Document Status
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {uploadedDocs}/{totalDocs} uploaded
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={completionPercentage}
          color={completionPercentage === 100 ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3, mb: 1 }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${completionPercentage}% Complete`}
            color={completionPercentage === 100 ? 'success' : 'primary'}
            size="small"
          />
          {approvedDocs > 0 && (
            <Chip
              label={`${approvedDocs} Approved`}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Individual document status */}
      {showDetails && (
        <Stack spacing={1}>
          {documentTypes.map(type => {
            const doc = documents[type.key as keyof typeof documents];
            return (
              <ImageStatus
                key={type.key}
                hasImage={doc?.hasImage || false}
                documentType={type.key as any}
                status={doc?.status as any}
                size="small"
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};